import { ChangeEvent, FC, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { PROMOTION_CATEGORY_OPTIONS, PROMOTION_STATUS_OPTIONS } from "components/promotions/constants"
import { Table } from "components/tables/Table"

import { tablePromotionsColumns } from "constants/data"
import { ALL_LANGUAGE_CODES, DEFAULT_LOCALE, getLanguageName } from "constants/languages"
import { TableAction } from "constants/type"

import { promotionsService } from "services/promotions/promotions.service"
import { PromotionCategory, PromotionListItem, PromotionStatus } from "services/promotions/type"

interface PromotionsProps {
  /**
   * Supplied when the list is embedded in the CMS Pages view, which keeps the
   * editor on that page instead of routing away to the standalone screen.
   */
  onCreate?: () => void
  onOpen?: (id: string) => void
}

export const Promotions: FC<PromotionsProps> = ({ onCreate, onOpen }) => {
  const navigate = useNavigate()
  const { t } = useTranslation("translation", { useSuspense: false })
  const [status, setStatus] = useState<"" | PromotionStatus>("")
  const [category, setCategory] = useState<"" | PromotionCategory>("")
  const [search, setSearch] = useState("")
  const [locale, setLocale] = useState<string>(DEFAULT_LOCALE)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [deleteTarget, setDeleteTarget] = useState<PromotionListItem | null>(null)

  const LOCALE_OPTIONS = useMemo(() => {
    const codes = (ALL_LANGUAGE_CODES || []).filter(Boolean)
    return codes
      .map((code) => ({
        value: code,
        name: `${getLanguageName(code)} (${code.toUpperCase()})`
      }))
      .sort((a, b) => {
        if (a.value === DEFAULT_LOCALE) return -1
        if (b.value === DEFAULT_LOCALE) return 1
        return a.name.localeCompare(b.name)
      })
  }, [])

  const filters = useMemo(
    () => ({
      status,
      category,
      locale,
      q: search.trim()
    }),
    [status, category, search, locale]
  )

  const handleCreateClick = () => (onCreate ? onCreate() : navigate("/promotions/create"))
  const handleRowClick = (row: PromotionListItem) => (onOpen ? onOpen(row.id) : navigate(`/promotions/${row.id}`))

  const tableActions: TableAction[] = [
    { name: "Edit", handler: (row) => handleRowClick(row) },
    { name: "Delete", buttonColor: "danger", handler: (row) => setDeleteTarget(row) }
  ]

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="min-w-[260px]">
          <label className="label block mb-1">{t("promotions.locale")}</label>
          <DropdownMenu
            title=""
            options={LOCALE_OPTIONS}
            value={locale}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              setLocale(e.target.value)
              setTableUpdatedAt(Date.now())
            }}
            rounded
          />
        </div>

        <div className="min-w-[200px]">
          <label className="label block mb-1">{t("promotions.status")}</label>
          <DropdownMenu
            title=""
            options={PROMOTION_STATUS_OPTIONS.map((o) => ({ value: o.value, name: o.name }))}
            value={status}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              setStatus(e.target.value as any)
              setTableUpdatedAt(Date.now())
            }}
            rounded
          />
        </div>

        <div className="min-w-[200px]">
          <label className="label block mb-1">{t("promotions.category")}</label>
          <DropdownMenu
            title=""
            options={PROMOTION_CATEGORY_OPTIONS.map((o) => ({ value: o.value, name: o.name }))}
            value={category}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              setCategory(e.target.value as any)
              setTableUpdatedAt(Date.now())
            }}
            rounded
          />
        </div>

        <div className="min-w-[220px]">
          <label className="label block mb-1">{t("form.search")}</label>
          <input
            className="input input-bordered w-full"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setTableUpdatedAt(Date.now())
            }}
            placeholder={t("promotions.search")}
          />
        </div>

        <button className="btn btn-primary ml-auto" onClick={handleCreateClick}>
          {t("promotions.createPromotion")}
        </button>
      </div>

      <Table
        title={t("promotions.tableTitle")}
        handler={promotionsService.getPromotionsTable}
        columns={tablePromotionsColumns}
        filters={filters}
        onRowClick={handleRowClick}
        updatedAt={tableUpdatedAt}
        actions={tableActions}
        isColumnsFilterable
      />

      {deleteTarget && (
        <ModalConfirmAction
          data={deleteTarget}
          handler={() => setDeleteTarget(null)}
          text={t("promotions.confirmDelete")}
          actionText="Delete"
          actionHandler={async (target: PromotionListItem) => {
            try {
              await promotionsService.deletePromotion(target.id)
              toast.success("Promotion deleted")
              setDeleteTarget(null)
              setTableUpdatedAt(Date.now())
            } catch (err: any) {
              toast.error(err?.message ?? "Failed to delete promotion")
            }
          }}
        />
      )}
    </div>
  )
}
